import { Dashboard, TIME_ZONE, formatBRL, formatTime } from '@deizi/shared';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../components/Icon';
import { PageHead } from '../components/PageHead';
import { Alert, ErrorBox } from '../components/States';
import { api } from '../lib/api';
import { formatAge, useBackupStatus } from '../lib/backups-api';
import { formatHm } from '../lib/numbers';
import { useIsAdmin, useMe } from '../lib/session';
import { STEP_FOR_ITEM, useSetupStatus } from '../lib/setup-api';
import { useIsPhone } from '../lib/use-media';

const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/** Weekday of a `YYYY-MM-DD` (Sakamoto), without touching the clock. */
function weekday(iso: string): string {
  const t = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
  let y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  if (m < 3) y -= 1;
  return WEEKDAYS[
    (y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) + t[m - 1]! + d) % 7
  ]!;
}

const dm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const dmy = (iso: string) => `${dm(iso)}/${iso.slice(0, 4)}`;

function greeting(now = new Date()): string {
  const hour = Number(
    new Intl.DateTimeFormat('pt-BR', {
      timeZone: TIME_ZONE,
      hour: '2-digit',
      hourCycle: 'h23',
    }).format(now),
  );
  if (hour < 12) return 'Bom dia';
  if (hour < 18) return 'Boa tarde';
  return 'Boa noite';
}

/** The clock, refreshed every 30 s (printer countdowns). */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

const useDashboard = () =>
  useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get('/dashboard', Dashboard),
    refetchInterval: 60_000,
  });

/** Backups stale, failing or never run: shown to both roles (the spec's 36-hour rule). */
function BackupAlert({ isAdmin }: { isAdmin: boolean }) {
  const status = useBackupStatus();
  const s = status.data;
  if (!s) return null;
  const failing = s.destinations.filter((d) => d.lastStatus === 'failed');
  const problem = !s.lastComplete
    ? 'Ainda não há backup completo. Faça o primeiro antes de cadastrar dados reais.'
    : s.lastComplete.stale
      ? `O último backup completo foi há ${formatAge(s.lastComplete.ageHours)}.`
      : failing.length
        ? `O backup falhou em ${failing.map((d) => d.name).join(' e ')}.`
        : !s.worker.online
          ? 'O serviço de backup está parado.'
          : null;
  if (!problem) return null;
  return (
    <Alert tone="err">
      <b>Backups.</b> {problem}{' '}
      {isAdmin ? <Link to="/configuracoes/dados">Resolver</Link> : 'Avise o Lucas.'}
    </Alert>
  );
}

function Tile({
  label,
  count,
  sub,
  to,
  tone,
}: {
  label: string;
  count: number;
  sub: string | null;
  to: string;
  tone?: 'err' | 'warn';
}) {
  return (
    <Link
      to={to}
      className="card col"
      style={{
        gap: 4,
        minHeight: 104,
        color: 'inherit',
        textDecoration: 'none',
        background: tone === 'err' ? 'var(--err-bg)' : 'var(--sunk)',
        borderColor: 'transparent',
      }}
    >
      <span className="xs muted">{label}</span>
      <span
        className="fr tab"
        style={{ fontSize: 32, color: tone === 'err' ? 'var(--err)' : undefined }}
      >
        {count}
      </span>
      {sub ? (
        <span
          className="xs"
          style={{
            color: tone === 'err' ? 'var(--err)' : tone === 'warn' ? 'var(--warn)' : 'var(--ink2)',
            fontWeight: tone ? 500 : undefined,
          }}
        >
          {sub}
        </span>
      ) : null}
    </Link>
  );
}

function needSub(
  t: Dashboard['needs']['toPrint'],
  today: string,
  kind: 'print' | 'ship',
): { sub: string | null; tone?: 'err' } {
  if (!t.count) return { sub: null };
  if (t.late && t.nearestOn) return { sub: `atrasado desde ${dm(t.nearestOn)}`, tone: 'err' };
  if (kind === 'ship' && t.single && t.nearestOn)
    return { sub: `${t.single.number} · prazo de envio ${dm(t.nearestOn)}` };
  if (!t.nearestOn) return { sub: null };
  return {
    sub:
      t.nearestOn === today
        ? 'próximo prazo hoje'
        : `próximo prazo ${t.nearestOn === tomorrowOf(today) ? 'amanhã' : dm(t.nearestOn)}`,
  };
}

function tomorrowOf(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const next = new Date(Date.UTC(y!, m! - 1, d! + 1));
  return next.toISOString().slice(0, 10);
}

function NeedsCard({ d, phone }: { d: Dashboard; phone: boolean }) {
  const print = needSub(d.needs.toPrint, d.today, 'print');
  const pack = needSub(d.needs.toPack, d.today, 'print');
  const ship = needSub(d.needs.toShip, d.today, 'ship');
  const q = d.needs.quotes;
  const tiles = (
    <div
      style={{
        display: 'grid',
        gap: 12,
        gridTemplateColumns: phone ? '1fr 1fr' : 'repeat(auto-fit, minmax(170px, 1fr))',
      }}
    >
      <Tile
        label={phone ? 'Imprimir' : 'Para imprimir'}
        count={d.needs.toPrint.count}
        sub={print.sub}
        tone={print.tone}
        to="/fila"
      />
      <Tile
        label={phone ? 'Embalar' : 'Para embalar'}
        count={d.needs.toPack.count}
        sub={pack.sub}
        tone={pack.tone}
        to={d.needs.toPack.single ? `/pedidos/${d.needs.toPack.single.id}` : '/pedidos'}
      />
      <Tile
        label={phone ? 'Enviar' : 'Para enviar'}
        count={d.needs.toShip.count}
        sub={ship.sub}
        tone={ship.tone}
        to={d.needs.toShip.single ? `/pedidos/${d.needs.toShip.single.id}` : '/pedidos'}
      />
      <Tile
        label={phone ? 'Responder' : 'Orçamentos sem resposta'}
        count={q.count}
        sub={
          q.expiringSoon
            ? `${q.expiringSoon} ${q.expiringSoon === 1 ? 'expira' : 'expiram'} até amanhã`
            : null
        }
        tone={q.expiringSoon ? 'warn' : undefined}
        to="/orcamentos"
      />
    </div>
  );
  if (d.calm)
    return (
      <section className="card row wrap" style={{ gap: 18, alignItems: 'center' }}>
        <div className="emptyart" aria-hidden="true" style={{ margin: 0 }} />
        <div className="col grow" style={{ gap: 6, minWidth: 220 }}>
          <h2>Tudo em dia</h2>
          <p className="muted">
            Nenhum pedido para imprimir, embalar ou enviar, e nenhum orçamento esperando resposta.
          </p>
          <Link className="btn sm g" style={{ alignSelf: 'flex-start', padding: 0 }} to="/pedidos">
            Ver pedidos
          </Link>
        </div>
      </section>
    );
  return phone ? (
    <div className="col" style={{ gap: 10 }}>
      <h2 style={{ fontSize: 18 }}>Para fazer hoje</h2>
      {tiles}
    </div>
  ) : (
    <section className="card col" style={{ gap: 14 }}>
      <h2>Para fazer hoje</h2>
      {tiles}
    </section>
  );
}

function PrinterCard({
  p,
  now,
  phone,
}: {
  p: Dashboard['printers'][number];
  now: number;
  phone: boolean;
}) {
  if (p.current) {
    const ends = p.current.endsAt ? new Date(p.current.endsAt).getTime() : null;
    const left = ends ? Math.max(0, Math.round((ends - now) / 1000)) : null;
    const pct = p.current.progressPct ?? 0;
    return (
      <Link
        to="/fila"
        className="card col"
        style={{ gap: 10, color: 'inherit', textDecoration: 'none' }}
        aria-label={`${p.name}: imprimindo ${p.current.label}, abrir fila`}
      >
        <div className="row between">
          <h2>{phone ? p.name : `${p.name} agora`}</h2>
          <span className="badge info">Imprimindo</span>
        </div>
        <p>
          {p.current.label}
          {phone ? null : <span className="xs muted"> · {p.current.number}</span>}
        </p>
        <div className="layers" style={{ height: 16 }}>
          <div className="fill" style={{ width: `${Math.min(100, pct)}%` }} />
          <div className="nozzle" style={{ left: `${Math.min(100, pct)}%` }} />
        </div>
        <div className="row between sm">
          <span className="muted" role="timer">
            {phone ? '' : `${pct}% · `}
            {left !== null ? `faltam ${formatHm(left)}` : ''}
          </span>
          {p.current.endsAt ? (
            <span style={{ fontWeight: 500 }}>
              {phone ? '' : 'termina '}
              {formatTime(p.current.endsAt)}
            </span>
          ) : null}
        </div>
        {p.next ? (
          <div className="kv sm">
            <span className="muted">Depois</span>
            <span className="v">
              {p.next.label}
              {phone ? '' : ` · ${formatHm(p.next.seconds)}`}
            </span>
          </div>
        ) : null}
      </Link>
    );
  }
  const statusText =
    p.status === 'maintenance'
      ? 'Manutenção'
      : p.status === 'out_of_service'
        ? 'Fora de serviço'
        : 'Livre';
  return (
    <section className="card col" style={{ gap: 10 }}>
      <div className="row between">
        <h2>{phone ? p.name : `${p.name} agora`}</h2>
        <span className={p.status === 'idle' ? 'badge' : 'badge warn'}>{statusText}</span>
      </div>
      {p.next ? (
        <div className="kv sm">
          <span className="muted">Próxima na fila</span>
          <span className="v">{p.next.label}</span>
        </div>
      ) : p.suggestion ? (
        <p className="sm muted">
          {p.suggestion.name} tem {p.suggestion.available} em pronta entrega; costuma vender{' '}
          {p.suggestion.perWeek.toLocaleString('pt-BR')} por semana.
        </p>
      ) : (
        <p className="sm muted">Nada na fila.</p>
      )}
      <Link
        className={phone ? 'btn sm' : 'btn'}
        style={{ alignSelf: 'flex-start' }}
        to="/fila?nova=1"
      >
        Imprimir para estoque
      </Link>
    </section>
  );
}

function MonthCard({ m, phone }: { m: Dashboard['month']; phone: boolean }) {
  const name = new Intl.DateTimeFormat('pt-BR', { month: 'long', timeZone: 'UTC' })
    .format(new Date(`${m.month}-15T12:00:00Z`))
    .replace(/^./, (c) => c.toUpperCase());
  const fill = Math.min(100, m.fillBps / 100);
  const bar = (
    <div className="layers" style={{ height: 22 }}>
      <div
        className="fill"
        style={{ width: `${fill}%`, borderRadius: fill >= 100 ? 5 : undefined }}
      />
      <div className="nozzle" style={{ left: fill >= 100 ? 'calc(100% - 8px)' : `${fill}%` }} />
    </div>
  );
  if (m.reached)
    return (
      <Link
        to="/relatorios"
        className="card col"
        style={{ gap: 10, color: 'inherit', textDecoration: 'none' }}
      >
        <div className="row between">
          <h2>{phone ? name : `${name} vs. equilíbrio`}</h2>
          <span className="badge ok">Acima do equilíbrio</span>
        </div>
        {bar}
        <p className="sm muted">
          Os custos fixos do mês já estão cobertos: o que entrar agora é lucro.
          {phone ? '' : ` Lucro até agora: ${formatBRL(m.netProfitCentavos)}.`}
        </p>
      </Link>
    );
  return (
    <section className="card col" style={{ gap: 10 }}>
      <div className="row between">
        <h2>{phone ? name : `${name} vs. equilíbrio`}</h2>
        {phone ? (
          <span className="sm muted">{Math.round(fill)}% do equilíbrio</span>
        ) : (
          <Link to="/relatorios" className="sm">
            Relatório
          </Link>
        )}
      </div>
      <p>
        <span className="fr tab" style={{ fontSize: phone ? 20 : 32 }}>
          {formatBRL(m.contributionCentavos)}
        </span>{' '}
        <span className="muted">de {formatBRL(m.fixedCentavos)}</span>
        {phone && m.unitsMissing ? (
          <span className="sm muted"> · faltam {m.unitsMissing} vendas</span>
        ) : null}
      </p>
      {bar}
      {phone ? null : (
        <div className="row wrap sm" style={{ gap: 16 }}>
          <span>
            Receita <b>{formatBRL(m.revenueCentavos)}</b>
          </span>
          <span>
            Custos variáveis <b>{formatBRL(m.variableCentavos)}</b>
          </span>
          <span>
            Lucro{' '}
            <b style={{ color: m.netProfitCentavos < 0 ? 'var(--err)' : 'var(--ok)' }}>
              {m.netProfitCentavos < 0
                ? `− ${formatBRL(-m.netProfitCentavos)}`
                : formatBRL(m.netProfitCentavos)}
            </b>
          </span>
        </div>
      )}
    </section>
  );
}

function SmallCard({
  title,
  link,
  children,
}: {
  title: string;
  link?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card col" style={{ gap: 8 }}>
      <div className="row between">
        <h3>{title}</h3>
        {link}
      </div>
      {children}
    </section>
  );
}

function quoteBadge(
  q: Dashboard['openQuotes'][number],
  today: string,
): { text: string; cls: string } {
  if (q.validUntil === today) return { text: 'expira hoje', cls: 'badge warn' };
  if (q.validUntil === tomorrowOf(today)) return { text: 'expira amanhã', cls: 'badge warn' };
  const sent = q.sentAt ? q.sentAt.slice(0, 10) : null;
  if (!sent) return { text: `válido até ${dm(q.validUntil)}`, cls: 'badge' };
  return { text: `enviado ${dm(sent)}`, cls: 'badge info' };
}

function when(iso: string, today: string): string {
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE }).format(new Date(iso));
  if (day === today) return formatTime(iso);
  if (tomorrowOf(day) === today) return 'ontem';
  return dm(day);
}

export function HomePage() {
  const me = useMe();
  const isAdmin = useIsAdmin();
  const phone = useIsPhone();
  const setup = useSetupStatus();
  const dash = useDashboard();
  const now = useNow();
  const first = me.name.split(' ')[0] ?? me.name;
  const missing = setup.data?.items.filter((i) => !i.done) ?? [];
  const firstMissing = missing[0];
  const d = dash.data;
  const quick = (
    <Link
      className="btn p"
      to="/orcamento-rapido"
      style={
        phone
          ? { height: 56, justifyContent: 'center', fontSize: 17 }
          : { height: 52, fontSize: 16 }
      }
    >
      <Icon name="calculator" size={20} />
      Orçamento rápido
    </Link>
  );
  return (
    <div className="col" style={{ gap: 22 }}>
      <PageHead
        crumb={d ? `${weekday(d.today)}, ${dmy(d.today)}` : 'Início'}
        title={`${greeting()}, ${first}`}
        actions={phone ? undefined : quick}
      />
      {firstMissing ? (
        <Alert tone="warn">
          <b>Configuração incompleta.</b>{' '}
          {missing.length === 1 ? 'Falta 1 item' : `Faltam ${missing.length} itens`} para calcular
          preços.{' '}
          {isAdmin ? (
            <Link to={`/configuracao-inicial?passo=${STEP_FOR_ITEM[firstMissing.key]}`}>
              Continuar configuração
            </Link>
          ) : (
            'Peça ao Admin para terminar a configuração.'
          )}
        </Alert>
      ) : null}
      {phone ? null : <BackupAlert isAdmin={isAdmin} />}
      {setup.data?.business === 'skipped' && isAdmin ? (
        <Alert>
          Os dados do negócio ficaram para depois: sem eles, o PDF do orçamento sai sem CNPJ.{' '}
          <Link to="/configuracao-inicial?passo=1">Completar agora</Link>
        </Alert>
      ) : null}
      {phone ? quick : null}
      {dash.isPending ? (
        <div className="col" aria-busy="true" style={{ gap: 14 }}>
          <div className="skel" style={{ height: 150 }} />
          <div className="skel" style={{ height: 150 }} />
        </div>
      ) : null}
      {dash.isError ? (
        <div className="card">
          <ErrorBox
            title="Não foi possível carregar o início"
            onRetry={() => void dash.refetch()}
          />
        </div>
      ) : null}
      {d ? (
        <>
          <NeedsCard d={d} phone={phone} />
          <div
            style={{
              display: 'grid',
              gap: 18,
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
              alignItems: 'start',
            }}
          >
            {d.printers.map((p) => (
              <PrinterCard key={p.id} p={p} now={now} phone={phone} />
            ))}
            <MonthCard m={d.month} phone={phone} />
            {phone ? null : (
              <SmallCard
                title="Orçamentos abertos"
                link={
                  <Link to="/orcamentos" className="sm">
                    Ver todos
                  </Link>
                }
              >
                {d.openQuotes.length === 0 ? (
                  <div className="col" style={{ gap: 8, alignItems: 'flex-start' }}>
                    <p className="sm muted">Nenhum orçamento esperando resposta.</p>
                    <Link className="btn sm" to="/orcamento-rapido">
                      Fazer orçamento
                    </Link>
                  </div>
                ) : (
                  d.openQuotes.map((q) => {
                    const b = quoteBadge(q, d.today);
                    return (
                      <Link
                        key={q.id}
                        to={`/orcamentos/${q.id}`}
                        className="kv sm"
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        <span style={{ minWidth: 0 }}>
                          <b className="tab">{q.number}</b>
                          <span className="xs muted" style={{ display: 'block' }}>
                            {q.what}
                            {q.who ? ` · ${q.who}` : ''}
                          </span>
                        </span>
                        <span className={b.cls}>{b.text}</span>
                      </Link>
                    );
                  })
                )}
              </SmallCard>
            )}
          </div>
          {phone ? null : (
            <div
              style={{
                display: 'grid',
                gap: 18,
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
                alignItems: 'start',
              }}
            >
              <SmallCard
                title="Estoque baixo"
                link={
                  <Link
                    to={d.lowStock.length ? '/filamentos/compras' : '/filamentos'}
                    className="sm"
                  >
                    {d.lowStock.length ? 'Lista de compras' : 'Filamentos'}
                  </Link>
                }
              >
                {d.lowStock.length === 0 ? (
                  <p className="sm muted">Nada abaixo do mínimo.</p>
                ) : null}
                {d.lowStock.map((s) => (
                  <Link
                    key={`${s.kind}-${s.name}`}
                    to={s.kind === 'filament' ? '/filamentos' : '/insumos'}
                    className="kv sm"
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    <span>{s.name}</span>
                    <span className="v" style={{ color: 'var(--warn)' }}>
                      {s.current.toLocaleString('pt-BR')}
                      {s.unit ? ` ${s.unit}` : ''} / {s.minimum.toLocaleString('pt-BR')}
                      {s.unit ? ` ${s.unit}` : ''}
                    </span>
                  </Link>
                ))}
              </SmallCard>
              <SmallCard
                title="Manutenção"
                link={
                  <Link to="/impressoras" className="sm">
                    Impressoras
                  </Link>
                }
              >
                {d.maintenance.length === 0 ? (
                  <p className="sm muted">Nenhuma manutenção ou parcela nos próximos dias.</p>
                ) : null}
                {d.maintenance.map((m) => (
                  <Link
                    key={m.label}
                    to={m.link}
                    className="kv sm"
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    <span>{m.label}</span>
                    <span className={m.tone === 'err' ? 'badge err' : 'badge warn'}>{m.badge}</span>
                  </Link>
                ))}
              </SmallCard>
              <SmallCard title="Atividade recente">
                {d.activity.length === 0 ? (
                  <p className="sm muted">
                    {d.lastActivityOn
                      ? `Nada novo desde ${weekday(d.lastActivityOn)}.`
                      : 'Nada por aqui ainda.'}
                  </p>
                ) : null}
                {d.activity.map((a) => (
                  <Link
                    key={`${a.at}-${a.text}`}
                    to={a.link}
                    className="kv sm"
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    <span style={{ color: a.tone === 'err' ? 'var(--err)' : undefined }}>
                      {a.text}
                    </span>
                    <span
                      className="v"
                      style={{ color: a.tone === 'err' ? 'var(--err)' : 'var(--ink2)' }}
                    >
                      {when(a.at, d.today)}
                    </span>
                  </Link>
                ))}
              </SmallCard>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
